import React from 'react'
import { useTranslation } from 'react-i18next'
import { Gem, Trophy } from 'lucide-react'
import { formatPrice, parseSkinName } from '../../../utils/formatters'
import { calcPrizeFund } from '../utils/rushdropFormat'

/**
 * Призы эвента Rush Drop.
 * Один элемент prizes = диапазон мест с одинаковым призом.
 * Главный приз не обязательно на 1-м месте — порядок по place_from, как пришёл.
 * dimmed — серое отображение (финал без участников, «призы сгорели»).
 */
const RushDropPrizeCard = ({ prize }) => {
  const { t } = useTranslation()
  const from = Number(prize?.place_from || 0)
  const to = Number(prize?.place_to || 0)
  const isRange = to > from
  const isSkin = prize?.kind === 'skin' && prize?.skin

  const placeLabel = isRange
    ? t('rushdrop.prizes.placesRange', 'Призы {{from}}–{{to}}', { from, to })
    : t('rushdrop.prizes.place', 'Приз {{place}}', { place: from })

  const skinName = isSkin ? parseSkinName(prize.skin.name) : null

  return (
    <div className="flex flex-col items-center rounded-2xl border border-white/10 bg-surface p-3 text-center">
      <span className="mb-2 rounded-md bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-text-secondary">
        {placeLabel}
      </span>
      {isSkin ? (
        <>
          {prize.skin.image ? (
            <img
              src={prize.skin.image}
              alt={prize.skin.name || ''}
              className="h-16 w-16 object-contain"
              loading="lazy"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-white/5">
              <Trophy size={24} className="text-accent-primary/60" />
            </div>
          )}
          <p className="mt-2 line-clamp-2 text-xs font-semibold leading-tight text-white">
            {skinName?.title || prize.skin.name}
          </p>
          {skinName?.subtitleCompact && (
            <p className="mt-0.5 line-clamp-1 text-[11px] text-text-secondary">
              {skinName.subtitleCompact}
            </p>
          )}
          <p className="mt-1 flex items-center gap-1 text-[11px] text-text-secondary">
            <Gem size={11} className="text-accent-primary" />
            ≈ {formatPrice(prize.crystals)}
          </p>
        </>
      ) : (
        <>
          <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-accent-primary/10">
            <Gem size={28} className="text-accent-primary" />
          </div>
          <p className="mt-2 text-sm font-bold tabular-nums text-white">
            {formatPrice(prize?.crystals)}
          </p>
          <p className="text-[11px] text-text-secondary">
            {t('rushdrop.prizes.crystals', 'кристаллов')}
          </p>
        </>
      )}
    </div>
  )
}

const RushDropPrizes = ({ prizes, dimmed = false }) => {
  const { t } = useTranslation()
  const list = Array.isArray(prizes) ? prizes : []

  if (list.length === 0) return null

  const fund = calcPrizeFund(list)
  // Сортируем по place_from — «первый элемент = главный приз» зашивать нельзя
  const sorted = [...list].sort((a, b) => Number(a?.place_from || 0) - Number(b?.place_from || 0))

  return (
    <section className={dimmed ? 'opacity-50 grayscale' : ''}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-bold text-white">
          <Trophy size={18} className="text-accent-primary" />
          {t('rushdrop.prizes.title', 'Призы')}
        </h2>
        {fund > 0 && (
          <span className="flex items-center gap-1 text-xs text-text-secondary">
            {t('rushdrop.prizes.fund', 'Фонд')}:
            <b className="flex items-center gap-1 tabular-nums text-accent-primary">
              <Gem size={12} />
              {formatPrice(fund)}
            </b>
          </span>
        )}
      </div>
      {/* Сетка без скролла: 2 колонки на мобилке, 3 на десктопе — видно все призы сразу */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {sorted.map((prize, index) => (
          <RushDropPrizeCard key={`${prize?.place_from}-${prize?.place_to}-${index}`} prize={prize} />
        ))}
      </div>
    </section>
  )
}

export default RushDropPrizes
